import { ReactNode } from 'react';
import { Input, Select } from '../atoms';
import type { SelectOption } from '../atoms';

/**
 * ListToolbar — search box + optional filter dropdowns above a server-side list.
 *
 * Pairs with useServerList (web/src/lib/useServerList.ts): the search input is
 * bound to the un-debounced value and each filter Select maps to a server-side
 * scalar filter param (status / re_pp / under_appeal …). Pure presentation —
 * value in, change out — so every list page renders the same control row.
 *
 * PRD 06.1 (server-side list search/sort/paginate).
 */

export interface ListToolbarFilter {
  /** Server query param + React key, e.g. 'status'. */
  key: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;         // "All statuses"
  options: SelectOption[];
  /** Keep the options in the order given (a status lifecycle, years) instead of alphabetical. */
  preserveOrder?: boolean;
  widthPx?: number;
}

export interface ListToolbarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  filters?: ListToolbarFilter[];
  /** Optional right-aligned slot (e.g. result count). */
  trailing?: ReactNode;
}

export function ListToolbar({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search…',
  filters = [],
  trailing,
}: ListToolbarProps) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--sp-3)',
      marginBottom: 'var(--sp-4)',
      flexWrap: 'wrap',
    }}>
      <div style={{ position: 'relative', flex: '1 1 260px', minWidth: 220, maxWidth: 420 }}>
        <Input
          type="search"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
        />
      </div>
      {filters.map((f) => (
        // `0 1 <w>px`: keep the desktop fixed width (no grow), but ALLOW the filter
        // to shrink + honor maxWidth:100% so a wide widthPx can never push past the
        // viewport edge at phone width (it shrinks / wraps) instead of overflowing.
        <div key={f.key} style={{ flex: `0 1 ${f.widthPx ?? 180}px`, minWidth: 0, maxWidth: '100%' }}>
          <Select
            options={f.options}
            preserveOrder={f.preserveOrder}
            placeholder={f.placeholder}
            value={f.value}
            onChange={(e) => f.onChange(e.target.value)}
            aria-label={f.placeholder}
          />
        </div>
      ))}
      {trailing && <div style={{ marginLeft: 'auto', color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>{trailing}</div>}
    </div>
  );
}
